// Fully deterministic PDF with HSN-wise discount apportioning and max-rate charges row.
// Extras in this version:
// - "Ledger" shows closing total from PartyLedgersView (expects CollectedData.closingBalanceSigned; + = DR, - = CR)
// - "Last Payment Date" and "Last Payment Amount" shown from CollectedData.lastPaymentDate / lastPaymentAmount

import React, { useEffect, useMemo } from "react";
import {
  Page,
  Text,
  View,
  Document,
  StyleSheet,
  Image,
} from "@react-pdf/renderer";
import cancelimage from "../import/cancelledOrder.jpg";
import appLogo from "../import/logo.png"; // top banner logo
import { baseURL } from "../import/ApiEndPoint/TestApi";

/* ---------------- styles ---------------- */
const styles = StyleSheet.create({
  page: { fontFamily: "Helvetica", padding: 30, position: "relative" },
  watermark: {
    position: "absolute",
    top: "30%",
    left: "25%",
    transform: "rotate(-18deg)",
    opacity: 0.3,
    zIndex: -1,
    width: "400px",
    height: "380px",
  },
  topNote: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    paddingVertical: 4,
    paddingHorizontal: 8,
    borderRadius: 4,
    marginBottom: 6,
  },
  topNoteText: {
    fontSize: 9,
    fontWeight: "bold",
    color: "#000000",
    textTransform: "uppercase",
  },
  topNoteLogo: {
    height: 10,
    width: 38,
    marginHorizontal: 4,
  },
  header: { fontSize: "8px", marginTop: "1px", marginBottom: "2px" },
  GSTIN: {
    fontSize: "10px",
    fontWeight: "bold",
    marginTop: "1px",
    marginBottom: "2px",
  },
  cnimage: { width: 300 },
});

/* ---------------- helpers ---------------- */
const N = (v) => (isNaN(Number(v)) ? 0 : Number(v));
const F2 = (n) => (Math.round(N(n) * 100) / 100).toFixed(2);
const lower = (s) => String(s || "").toLowerCase();
const parseRate = (r) => {
  if (r == null) return 0;
  if (typeof r === "number") return r;
  const m = String(r).match(/[\d.]+/g);
  return m ? Number(m.join("")) : 0;
};
const EPS = 0.05;
const d8 = (s) => (s ? String(s).split("T")[0] : "");

const firstValue = (...values) => {
  for (const value of values) {
    if (value === 0) return value;
    if (value !== undefined && value !== null && String(value).trim() !== "") {
      return value;
    }
  }
  return "";
};

const safeJSONParse = (raw, fallback = {}) => {
  try {
    const parsed = JSON.parse(raw || "");
    return parsed && typeof parsed === "object" ? parsed : fallback;
  } catch (_) {
    return fallback;
  }
};

const getProductObject = (item = {}) => {
  const candidates = [
    item?._productDetailsFromProductList,
    item?.productData,
    item?.productDetails,
    item?.product,
    typeof item?.productId === "object" ? item.productId : null,
  ];
  return candidates.find((x) => x && typeof x === "object") || {};
};

const getProductName = (item = {}) => {
  const product = getProductObject(item);
  const direct = firstValue(
    item?.Product_Title,
    item?.productName,
    item?.ProductName,
    item?.productTitle,
    item?.name,
    product?.Product_Title,
    product?.productName,
    product?.ProductName,
    product?.productTitle,
    product?.name,
    product?.title
  );
  if (direct) return String(direct).trim();

  return [
    firstValue(product?.category, item?.category),
    firstValue(product?.SubCategory, product?.subCategory, item?.SubCategory, item?.subCategory),
  ]
    .filter(Boolean)
    .join(" ")
    .trim();
};

const getProductHSN = (item = {}) => {
  const product = getProductObject(item);
  return firstValue(
    item?.HSN_Code,
    item?.HSN,
    item?.hsnCode,
    item?.HSNCode,
    item?.hsn_code,
    item?.hsn,
    product?.HSN_Code,
    product?.HSN,
    product?.hsnCode,
    product?.HSNCode,
    product?.hsn_code,
    product?.hsn
  );
};

const getProductGSTRate = (item = {}) => {
  const product = getProductObject(item);
  return firstValue(
    item?.gstPercentage,
    item?.GSTRate,
    item?.gstRate,
    product?.GSTRate,
    product?.gstPercentage,
    product?.gstRate
  );
};

const getPartyGST = (party = {}, invoice = {}) =>
  firstValue(
    party?.gstNumber,
    party?.GSTNumber,
    party?.GSTNo,
    party?.gstNo,
    party?.GSTIN,
    party?.gstin,
    party?.GST_Number,
    party?.gst_number,
    party?.gst,
    invoice?.gstNumber,
    invoice?.GSTNumber,
    invoice?.GSTNo,
    invoice?.gstNo,
    invoice?.GSTIN,
    invoice?.gstin
  );

const getPartyAddress = (party = {}) =>
  firstValue(
    party?.address,
    party?.registeredAddress,
    party?.billingAddress,
    party?.ownerAddress
  );

const getPartyMobile = (party = {}) =>
  firstValue(
    party?.contactNumber,
    party?.mobileNumber,
    party?.MobileNo,
    party?.mobile,
    party?.phone
  );

const normalizeCompanyObject = (value = {}) => {
  if (!value || typeof value !== "object") return {};
  return (
    value?.CompanyDetail ||
    value?.companyDetail ||
    value?.CompanyDetails ||
    value?.companyDetails ||
    value
  );
};

const resolveBank = (company = {}, fallbackCompany = {}) => {
  const primary = normalizeCompanyObject(company);
  const fallback = normalizeCompanyObject(fallbackCompany);
  const banks = Array.isArray(primary?.bankDetails)
    ? primary.bankDetails
    : Array.isArray(fallback?.bankDetails)
    ? fallback.bankDetails
    : [];

  const selectedBankName = firstValue(
    primary?.selectedBank?.bankName,
    primary?.selectedBank?.name,
    typeof primary?.selectedBank === "string" ? primary.selectedBank : "",
    fallback?.selectedBank?.bankName,
    fallback?.selectedBank?.name,
    typeof fallback?.selectedBank === "string" ? fallback.selectedBank : ""
  );

  const selectedFromList =
    banks.find((d) =>
      selectedBankName
        ? String(firstValue(d?.bankName, d?.name)).trim() === String(selectedBankName).trim()
        : false
    ) ||
    banks.find((d) => d?.isSelected || d?.selected || d?.default) ||
    banks[0] ||
    {};

  const selectedObject =
    primary?.selectedBank && typeof primary.selectedBank === "object"
      ? primary.selectedBank
      : fallback?.selectedBank && typeof fallback.selectedBank === "object"
      ? fallback.selectedBank
      : {};

  const source = { ...selectedFromList, ...selectedObject };

  return {
    bankName: firstValue(
      source?.bankName,
      source?.name,
      primary?.bankName,
      fallback?.bankName,
      selectedBankName
    ),
    accountNumber: firstValue(
      source?.accountNumber,
      source?.accountNo,
      source?.account_number,
      source?.Account_No,
      source?.acNo,
      primary?.accountNumber,
      primary?.accountNo,
      fallback?.accountNumber,
      fallback?.accountNo
    ),
    bankIFSC: firstValue(
      source?.bankIFSC,
      source?.ifscCode,
      source?.IFSC,
      source?.ifsc,
      source?.Ifsc_code,
      primary?.bankIFSC,
      primary?.ifscCode,
      primary?.IFSC,
      fallback?.bankIFSC,
      fallback?.ifscCode,
      fallback?.IFSC
    ),
    bankMicr: firstValue(
      source?.bankMicr,
      source?.micr,
      source?.MICR,
      source?.micrCode,
      source?.micrNo,
      primary?.bankMicr,
      primary?.micr,
      primary?.MICR,
      fallback?.bankMicr,
      fallback?.micr,
      fallback?.MICR
    ),
    upiId: firstValue(
      source?.upiId,
      source?.upiID,
      source?.UPI,
      source?.upi,
      source?.upi_id,
      primary?.upiId,
      primary?.upiID,
      primary?.UPI,
      fallback?.upiId,
      fallback?.upiID,
      fallback?.UPI
    ),
    gpayNumber: firstValue(
      source?.gpay_PhonepayNumber,
      source?.gpayPhonepayNumber,
      source?.gpayNumber,
      source?.gPayNumber,
      source?.googlePayNumber,
      source?.GPayNo,
      primary?.gpay_PhonepayNumber,
      primary?.gpayNumber,
      primary?.gPayNumber,
      primary?.googlePayNumber,
      primary?.GPayNo,
      fallback?.gpay_PhonepayNumber,
      fallback?.gpayNumber,
      fallback?.gPayNumber,
      fallback?.googlePayNumber,
      fallback?.GPayNo
    ),
  };
};

// robust image url builder (tries baseURL)
const imgURL = (filename) => {
  if (!filename) return "";
  return `${baseURL}/Images/${filename}`;
};

/* ---------------- component ---------------- */
const LastestInvoice = ({
  invoiceData = {},
  CollectedData = {},
  BilData = {},
}) => {
  // PDF rendering must have all values on the FIRST render. React effects do not
  // reliably run before @react-pdf serializes the document, so derive company
  // and bank data synchronously from localStorage here.
  const localCompany =
    typeof window !== "undefined"
      ? safeJSONParse(window.localStorage.getItem("Companydetail"), {})
      : {};
  const fallbackCompany = normalizeCompanyObject(BilData?.CompanyDetails || {});
  const localCompanyNormalized = normalizeCompanyObject(localCompany);
  const companyData = { ...fallbackCompany, ...localCompanyNormalized };
  const bank = resolveBank(localCompanyNormalized, fallbackCompany);
  const qr = firstValue(
    localCompanyNormalized?.qr_code,
    localCompanyNormalized?.qrCode,
    fallbackCompany?.qr_code,
    fallbackCompany?.qrCode
  );
  const upiid = bank?.upiId || "";
  const gpay = bank?.gpayNumber || "";

  /* ===== Party / transporter ===== */
  const party =
    invoiceData?.partyId && typeof invoiceData.partyId === "object"
      ? invoiceData.partyId
      : invoiceData?.customer && typeof invoiceData.customer === "object"
      ? invoiceData.customer
      : invoiceData?.party && typeof invoiceData.party === "object"
      ? invoiceData.party
      : {};
  const partyGST = getPartyGST(party, invoiceData);
  const partyAddress = getPartyAddress(party);
  const partyMobile = getPartyMobile(party);
  const assignTransporter = useMemo(() => {
    if (invoiceData?.transporter || invoiceData?.transporterName) {
      return {
        companyName:
          invoiceData?.transporter?.name || invoiceData?.transporterName || "",
        address:
          invoiceData?.transporter?.address ||
          invoiceData?.transporterAddress ||
          "",
        contactNumber:
          invoiceData?.transporter?.mobile ||
          invoiceData?.transporterMobile ||
          "",
      };
    }
    const t = party?.assignTransporter;
    if (!t) return null;
    if (Array.isArray(t)) return t[0] || null;
    if (typeof t === "object") return t;
    return null;
  }, [party, invoiceData]);

  /* ===== Inputs: items, discounts, charges ===== */
  const items = Array.isArray(invoiceData?.orderItems)
    ? invoiceData.orderItems
    : [];

  const discRows = Array.isArray(invoiceData?.discountDetails)
    ? invoiceData.discountDetails.filter((d) => lower(d?.type) === "discount")
    : [];
  const chgRows = Array.isArray(invoiceData?.discountDetails)
    ? invoiceData.discountDetails.filter((d) => lower(d?.type) === "charges")
    : [];

  const sumVal = (arr) =>
    arr.reduce(
      (acc, d) =>
        acc + N(d?.amount ?? d?.discountedAmount ?? d?.discountedValue ?? 0),
      0
    );

  const discountsTotal = sumVal(discRows);
  const chargesTotal = sumVal(chgRows);

  /* ===== Group items by HSN + Rate ===== */
  const grouped = useMemo(() => {
    const map = new Map();
    for (const it of items) {
      const hsn = getProductHSN(it) || "NA";
      const rate = parseRate(getProductGSTRate(it)) || 0;
      const key = `${hsn}|${rate}`;
      const base = N(it?.qty) * N(it?.price); // basic = qty × price
      if (!map.has(key)) {
        map.set(key, { hsn, rate, baseTaxable: 0 });
      }
      map.get(key).baseTaxable += base;
    }
    return Array.from(map.values());
  }, [items]);

  const basicTotalBeforeDiscount = grouped.reduce(
    (a, g) => a + N(g.baseTaxable),
    0
  );

  const maxGSTFromItems = grouped.reduce((m, g) => Math.max(m, N(g.rate)), 0);
  const maxGST = maxGSTFromItems || N(invoiceData?.maxGstPercentage) || 0;

  /* ===== Pro-rate discounts; charges as separate "HSN row" ===== */
  const isIGST = !!N(invoiceData?.igstTaxType);

  const hsnRows = useMemo(() => {
    const rows = [];

    // 1) Item HSN rows (discounts prorated)
    for (const g of grouped) {
      const share =
        basicTotalBeforeDiscount > 0
          ? N(g.baseTaxable) / basicTotalBeforeDiscount
          : 0;
      const discountAlloc = N(discountsTotal) * share;
      const netTaxable = Math.max(0, N(g.baseTaxable) - discountAlloc);

      const rate = N(g.rate);
      if (netTaxable <= 0 && rate <= 0) continue;

      if (isIGST) {
        const igst = (netTaxable * rate) / 100;
        rows.push({
          kind: "ITEM",
          hsn: g.hsn,
          rate,
          taxable: netTaxable,
          cgst: 0,
          sgst: 0,
          igst,
          total: netTaxable + igst,
        });
      } else {
        const half = rate / 2;
        const cgst = (netTaxable * half) / 100;
        const sgst = (netTaxable * half) / 100;
        rows.push({
          kind: "ITEM",
          hsn: g.hsn,
          rate,
          taxable: netTaxable,
          cgst,
          sgst,
          igst: 0,
          total: netTaxable + cgst + sgst,
        });
      }
    }

    // 2) Charges row (one line, taxed at highest rate)
    if (N(chargesTotal) > 0) {
      const rate = maxGST;
      const taxable = N(chargesTotal);
      const label = `CHARGES @ ${F2(rate)}%`;
      if (isIGST) {
        const igst = (taxable * rate) / 100;
        rows.push({
          kind: "CHARGES",
          hsn: label,
          rate,
          taxable,
          cgst: 0,
          sgst: 0,
          igst,
          total: taxable + igst,
        });
      } else {
        const half = rate / 2;
        const cgst = (taxable * half) / 100;
        const sgst = (taxable * half) / 100;
        rows.push({
          kind: "CHARGES",
          hsn: label,
          rate,
          taxable,
          cgst,
          sgst,
          igst: 0,
          total: taxable + cgst + sgst,
        });
      }
    }

    // Normalize to 2 decimals
    return rows.map((r) => ({
      ...r,
      taxable: N(F2(r.taxable)),
      cgst: N(F2(r.cgst)),
      sgst: N(F2(r.sgst)),
      igst: N(F2(r.igst)),
      total: N(F2(r.total)),
    }));
  }, [
    grouped,
    discountsTotal,
    chargesTotal,
    isIGST,
    basicTotalBeforeDiscount,
    maxGST,
  ]);

  /* ===== Totals from HSN table ===== */
  const hsnTaxableSum = hsnRows.reduce((a, r) => a + N(r.taxable), 0);
  const hsnCGST = hsnRows.reduce((a, r) => a + N(r.cgst), 0);
  const hsnSGST = hsnRows.reduce((a, r) => a + N(r.sgst), 0);
  const hsnIGST = hsnRows.reduce((a, r) => a + N(r.igst), 0);
  const hsnPreRoundGrand = hsnRows.reduce((a, r) => a + N(r.total), 0);

  /* ===== RoundOff + Final Grand ===== */
  const providedRoundOff = N(invoiceData?.roundOff);
  const providedGrand = N(invoiceData?.grandTotal);

  const grossBeforeRound = N(F2(hsnPreRoundGrand));
  const computedGrandTotal = Math.round(grossBeforeRound); // nearest rupee
  const computedRoundOff = N(F2(computedGrandTotal - grossBeforeRound));
  const computedFinalGrand = computedGrandTotal;

  /* ===== Cross-checks & popup ===== */
  useEffect(() => {
    const providedPreRound = providedGrand - providedRoundOff;

    const expectedTaxable =
      Math.max(0, basicTotalBeforeDiscount - discountsTotal) + chargesTotal;
    const taxableMismatch = Math.abs(expectedTaxable - hsnTaxableSum) > EPS;

    const preRoundMismatch =
      isFinite(providedPreRound) &&
      Math.abs(providedPreRound - hsnPreRoundGrand) > EPS;

    if (taxableMismatch || preRoundMismatch) {
      try {
        if (typeof window !== "undefined" && window?.swal) {
          window.swal({
            title: "Totals Mismatch",
            text:
              (taxableMismatch
                ? "HSN taxable != (Basic − Discounts + Charges). "
                : "") +
              (preRoundMismatch
                ? "Grand total (before roundoff) != HSN total."
                : ""),
            icon: "error",
          });
        } else if (typeof window !== "undefined") {
          window.alert(
            (taxableMismatch
              ? "HSN taxable != (Basic − Discounts + Charges). "
              : "") +
              (preRoundMismatch
                ? "Grand total (before roundoff) != HSN total."
                : "")
          );
        }
      } catch (_) {}
    }
  }, [
    providedGrand,
    providedRoundOff,
    hsnPreRoundGrand,
    hsnTaxableSum,
    basicTotalBeforeDiscount,
    discountsTotal,
    chargesTotal,
  ]);

  /* ===== Display helpers ===== */
  const infoTextStyle = {
    fontSize: "8px",
    fontWeight: "normal",
    color: "black",
    marginBottom: "4px",
    textTransform: "uppercase",
  };

  const safeCompanyName =
    companyData?.name || BilData?.CompanyDetails?.name || "";
  const safeCompanyAddr =
    companyData?.address || BilData?.CompanyDetails?.address || "";
  const safeCompanyEmail =
    companyData?.email || BilData?.CompanyDetails?.email || "";
  const safeCompanyMobile =
    companyData?.mobileNo || BilData?.CompanyDetails?.mobileNo || "";
  const safeCompanyGST =
    companyData?.gstNo || BilData?.CompanyDetails?.gstNo || "";

  /* ===== Ledger & Last Payment (from PartyLedgersView via CollectedData) =====
     Expected inputs:
     - CollectedData.closingBalanceSigned: Number (preferred). + = DR, - = CR.
       (If unavailable, we look for CollectedData.closingBalance (absolute) with
        CollectedData.closingSide "DR"/"CR". As last resort, invoiceData.lastLedgerBalance Number.)
     - CollectedData.lastPaymentDate: ISO date string
     - CollectedData.lastPaymentAmount: Number
  */
  let ledgerSigned = null;
  if (
    typeof CollectedData?.closingBalanceSigned === "number" &&
    !isNaN(CollectedData.closingBalanceSigned)
  ) {
    ledgerSigned = CollectedData.closingBalanceSigned;
  } else if (
    typeof CollectedData?.closingBalance === "number" &&
    !isNaN(CollectedData.closingBalance)
  ) {
    const side = String(CollectedData?.closingSide || "").toUpperCase();
    ledgerSigned =
      side === "CR"
        ? -Math.abs(CollectedData.closingBalance)
        : Math.abs(CollectedData.closingBalance);
  } else if (
    typeof invoiceData?.lastLedgerBalance === "number" &&
    !isNaN(invoiceData.lastLedgerBalance)
  ) {
    // Legacy fallback (no side info, assume DR for >=0 else CR if negative was set)
    ledgerSigned = invoiceData.lastLedgerBalance;
  }

  const normalize = (s) => (s ?? "").toString().trim();
  const hasVal = (s) => {
    const t = normalize(s).toLowerCase();
    return t && !["-", "na", "n/a", "null", "undefined"].includes(t);
  };

  const ledgerStr =
    ledgerSigned === null || isNaN(ledgerSigned)
      ? ""
      : `${Math.abs(ledgerSigned).toFixed(2)} ${
          ledgerSigned >= 0 ? "DR" : "CR"
        }`;

  const lastPayDate = CollectedData?.lastPaymentDate
    ? d8(CollectedData.lastPaymentDate)
    : "";
  const lastPayAmt =
    typeof CollectedData?.lastPaymentAmount === "number" &&
    !isNaN(CollectedData.lastPaymentAmount)
      ? F2(CollectedData.lastPaymentAmount)
      : "";

  const displayInvoiceNo = String(
    invoiceData?.invoiceId || invoiceData?.orderNo || ""
  );
  const amountInWords = (BilData && BilData.wordsNumber) || "";

  // Guard minimal data
  const isDataMissing =
    !invoiceData || Object.keys(invoiceData).length === 0 || !companyData;
  if (isDataMissing) {
    return (
      <Document>
        <Page size="A4">
          <Text>Loading invoice data...</Text>
        </Page>
      </Document>
    );
  }

  /* ====== computed logo/sign/qr urls ====== */
  const logoFile = companyData?.logo || BilData?.CompanyDetails?.logo || "";
  const logoSrc = logoFile ? imgURL(logoFile) : "";
  const signFile =
    companyData?.signature || BilData?.CompanyDetails?.signature || "";
  const signSrc = signFile ? imgURL(signFile) : "";
  const qrFile = qr || BilData?.CompanyDetails?.qr_code || "";
  const qrSrc = qrFile ? imgURL(qrFile) : "";

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {invoiceData?.canceledInvoice && (
          <Image src={cancelimage} style={styles.watermark} />
        )}

        {/* top banner */}
        <View style={styles.topNote}>
          <Text style={styles.topNoteText}>This invoice is generated in</Text>
          <Image src={appLogo} style={styles.topNoteLogo} />
          <Text style={styles.topNoteText}>Soft</Text>
        </View>

        <View>
          {/* ===== Header (Logo / Company / QR) ===== */}
          <View
            style={{
              flexDirection: "row",
              border: "1px solid black",
              height: "110px",
            }}
          >
            {/* Logo */}
            <View
              style={{
                width: "25%",
                padding: "5px",
                borderRight: "1px solid black",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              {logoSrc ? (
                <Image
                  style={{ width: "100%", maxWidth: "100px" }}
                  src={logoSrc}
                />
              ) : (
                <Text style={{ fontSize: 9, textTransform: "uppercase" }}>
                  LOGO
                </Text>
              )}
            </View>

            {/* Company block */}
            <View
              style={{
                padding: "10px",
                width: "50%",
                borderRight: "1px solid black",
              }}
            >
              <View style={{ paddingBottom: "3px" }}>
                <Text
                  style={{
                    fontSize: "13px",
                    fontWeight: "bold",
                    color: "black",
                    textAlign: "center",
                    textTransform: "uppercase",
                  }}
                >
                  {safeCompanyName}
                </Text>
                <Text
                  style={{
                    fontSize: "10px",
                    marginTop: "5px",
                    marginBottom: "2px",
                    textAlign: "center",
                    textTransform: "uppercase",
                  }}
                >
                  {safeCompanyAddr} {safeCompanyMobile}
                </Text>
                <Text
                  style={{
                    fontSize: "10px",
                    marginTop: "5px",
                    marginBottom: "2px",
                    textAlign: "center",
                    textTransform: "uppercase",
                  }}
                >
                  {safeCompanyEmail}
                </Text>
                <Text
                  style={{
                    fontSize: "10px",
                    marginTop: "5px",
                    marginBottom: "2px",
                    textAlign: "center",
                    textTransform: "uppercase",
                  }}
                >
                  {safeCompanyGST}
                </Text>
                <Text style={styles.header}></Text>
                <Text style={styles.header}></Text>
              </View>
            </View>

            {/* QR */}
            <View
              style={{
                padding: 0,
                width: "25%",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "flex-start",
                textAlign: "center",
                color: "black",
              }}
            >
              <Text
                style={{ marginBottom: 4, fontWeight: "bold", fontSize: 10 }}
              >
                QR CODE
              </Text>
              {qrSrc ? (
                <Image style={{ width: "90px", height: "90px" }} src={qrSrc} />
              ) : (
                <Text style={{ fontSize: 9 }}>NO QR</Text>
              )}
              <Text style={{ fontSize: 8, marginTop: 2 }}>
                FOR {safeCompanyName}
              </Text>
            </View>
          </View>

          {/* ===== Bill To / Ship To / Meta & Transporter ===== */}
          <View
            style={{
              flexDirection: "row",
              borderBottom: "1px solid black",
              borderLeft: "1px solid black",
              borderRight: "1px solid black",
              minHeight: "126px",
            }}
          >
            <View style={{ borderRight: "1px solid black", width: "100%" }}>
              <View style={{ flexDirection: "row" }}>
                {/* Bill To */}
                <View
                  style={{
                    width: "30%",
                    borderRight: "1px solid black",
                    padding: "8px",
                  }}
                >
                  {party?.CompanyName ? (
                    <Text
                      style={{
                        fontSize: "8px",
                        fontWeight: "bold",
                        marginBottom: "6px",
                        textTransform: "uppercase",
                      }}
                    >
                      Bill To: {party.CompanyName}
                    </Text>
                  ) : (
                    <Text
                      style={{
                        fontSize: "8px",
                        fontWeight: "bold",
                        marginBottom: "6px",
                        textTransform: "uppercase",
                      }}
                    >
                      Bill To:
                    </Text>
                  )}

                  {partyAddress && (
                    <Text style={infoTextStyle}>{partyAddress}</Text>
                  )}
                  {(party?.City || party?.State) && (
                    <Text style={infoTextStyle}>
                      {party.City || ""} {party.City && party.State ? "-" : ""}{" "}
                      {party.State || ""}
                    </Text>
                  )}
                  {party?.email && (
                    <Text style={infoTextStyle}>{party.email}</Text>
                  )}
                  {partyMobile && (
                    <Text style={infoTextStyle}>{partyMobile}</Text>
                  )}
                  {partyGST && (
                    <Text style={infoTextStyle}>GSTIN: {partyGST}</Text>
                  )}

                  {(invoiceData?.salesPersonName ||
                    invoiceData?.userId?.firstName) && (
                    <Text style={{ ...infoTextStyle, marginTop: 6 }}>
                      SP Name:{" "}
                      {invoiceData?.salesPersonName ||
                        invoiceData?.userId?.firstName}
                    </Text>
                  )}
                </View>

                {/* Ship To */}
                <View
                  style={{
                    width: "30%",
                    borderRight: "1px solid black",
                    padding: "8px",
                  }}
                >
                  <Text
                    style={{
                      fontSize: "8px",
                      fontWeight: "bold",
                      marginBottom: "6px",
                      textTransform: "uppercase",
                    }}
                  >
                    Ship To: {party?.CompanyName || ""}
                  </Text>

                  {partyAddress && (
                    <Text style={infoTextStyle}>{partyAddress}</Text>
                  )}
                  {(party?.City || party?.State) && (
                    <Text style={infoTextStyle}>
                      {party.City || ""} {party.City && party.State ? "-" : ""}{" "}
                      {party.State || ""}
                    </Text>
                  )}
                  {party?.email && (
                    <Text style={infoTextStyle}>{party.email}</Text>
                  )}
                  {partyMobile && (
                    <Text style={infoTextStyle}>{partyMobile}</Text>
                  )}
                  {partyGST && (
                    <Text style={infoTextStyle}>GSTIN: {partyGST}</Text>
                  )}

                  {(invoiceData?.salesPersonName ||
                    invoiceData?.userId?.firstName) && (
                    <Text style={{ ...infoTextStyle, marginTop: 6 }}>
                      SP Name:{" "}
                      {invoiceData?.salesPersonName ||
                        invoiceData?.userId?.firstName}
                    </Text>
                  )}
                </View>

                {/* Invoice meta + transporter */}
                <View style={{ width: "40%" }}>
                  <View
                    style={{
                      flexDirection: "row",
                      minHeight: "16px",
                      borderBottom: "1px solid black",
                    }}
                  >
                    <View style={{ width: "60%" }}>
                      <Text
                        style={{
                          fontSize: "8px",
                          fontWeight: "1000",
                          padding: "3px",
                          textTransform: "uppercase",
                        }}
                      >
                        {(invoiceData?.DeliveryType || "") === "Local"
                          ? `Challan No: ${displayInvoiceNo}`
                          : (invoiceData?.status || "").toLowerCase() ===
                            "completed"
                          ? `Invoice No: ${displayInvoiceNo}`
                          : `Challan No: ${displayInvoiceNo}`}
                      </Text>
                    </View>

                    <View style={{ width: "40%" }}>
                      <Text
                        style={{
                          fontSize: "8px",
                          fontWeight: "1000",
                          padding: "3px",
                          textTransform: "uppercase",
                        }}
                      >
                        Date: {d8(invoiceData?.date)}
                      </Text>
                    </View>
                  </View>

                  <View style={{ paddingTop: 2 }}>
                    {N(invoiceData?.grandTotal) > 50000 ? (
                      <View style={{ width: "100%" }}>
                        <Text
                          style={{
                            fontSize: 8,
                            fontWeight: 1000,
                            padding: 3,
                            textTransform: "uppercase",
                            borderWidth: 1,
                            borderColor: "black",
                            borderStyle: "solid",
                          }}
                        >
                          eway bill: {(invoiceData?.ARN || "").toString()}
                        </Text>
                      </View>
                    ) : null}

                    {!!ledgerStr && (
                      <View style={{ width: "100%" }}>
                        <Text
                          style={{
                            fontSize: "8px",
                            fontWeight: "1000",
                            padding: "3px",
                            textTransform: "uppercase",
                          }}
                        >
                          Ledger (Closing): {ledgerStr}
                        </Text>
                      </View>
                    )}

                    {(lastPayDate || lastPayAmt) && (
                      <View style={{ width: "100%" }}>
                        <Text
                          style={{
                            fontSize: "8px",
                            fontWeight: "1000",
                            padding: "3px",
                            textTransform: "uppercase",
                          }}
                        >
                          Last Payment: {lastPayDate || "-"}
                          {lastPayAmt ? ` — ₹${lastPayAmt}` : ""}
                        </Text>
                      </View>
                    )}
                  </View>

                  {/* Transporter (always show labels; fallbacks to "-") */}
                  <View
                    style={{
                      flexDirection: "row",
                      minHeight: "16px",
                      borderTop: "1px solid black",
                    }}
                  >
                    <View style={{ width: "100%" }}>
                      <Text
                        style={{
                          fontSize: "8px",
                          fontWeight: "1000",
                          padding: "3px",
                          textTransform: "uppercase",
                        }}
                      >
                        TRANSPORTER NAME:{" "}
                        {assignTransporter?.companyName || " - "}
                      </Text>
                    </View>
                  </View>
                  <View
                    style={{
                      flexDirection: "row",
                      minHeight: "16px",
                      borderTop: "1px solid black",
                    }}
                  >
                    <View style={{ width: "100%" }}>
                      <Text
                        style={{
                          fontSize: "8px",
                          fontWeight: "1000",
                          padding: "3px",
                          textTransform: "uppercase",
                        }}
                      >
                        ADDRESS: {assignTransporter?.address || " - "}
                      </Text>
                    </View>
                  </View>
                  <View
                    style={{
                      flexDirection: "row",
                      minHeight: "16px",
                      borderTop: "1px solid black",
                    }}
                  >
                    <View style={{ width: "100%" }}>
                      <Text
                        style={{
                          fontSize: "8px",
                          fontWeight: "1000",
                          padding: "3px",
                          textTransform: "uppercase",
                        }}
                      >
                        MOBILE NO: {assignTransporter?.contactNumber || " - "}
                      </Text>
                    </View>
                  </View>

                  {invoiceData?.CNNumber ? (
                    <View
                      style={{
                        flexDirection: "row",
                        minHeight: "16px",
                        borderTop: "1px solid black",
                      }}
                    >
                      <View style={{ width: "100%", display: "flex" }}>
                        <Text
                          style={{
                            fontSize: "8px",
                            fontWeight: "1000",
                            padding: "3px",
                            textTransform: "uppercase",
                          }}
                        >
                          CN No: {invoiceData?.CNNumber}
                        </Text>
                      </View>
                      <View style={{ width: "100%", display: "flex" }}>
                        <Text
                          style={{
                            fontSize: "8px",
                            fontWeight: "1000",
                            padding: "3px",
                            textTransform: "uppercase",
                          }}
                        >
                          CN qty: {invoiceData?.CNQty}
                        </Text>
                      </View>
                    </View>
                  ) : null}
                </View>
              </View>
            </View>
          </View>

          {/* ===== Line items: table header ===== */}
          <View
            style={{
              flexDirection: "row",
              borderBottom: "1px solid black",
              borderRight: "1px solid black",
              borderLeft: "1px solid black",
            }}
          >
            <View
              style={{
                width: "5%",
                borderRight: "1px solid black",
                paddingTop: "1px",
              }}
            >
              <Text
                style={{
                  fontSize: 8,
                  textAlign: "center",
                  textTransform: "uppercase",
                  paddingTop: 2,
                }}
              >
                s.no
              </Text>
            </View>

            <View
              style={{
                width: "40%",
                borderRight: "1px solid black",
                paddingTop: "1px",
              }}
            >
              <Text
                style={{
                  fontSize: 8,
                  textAlign: "center",
                  textTransform: "uppercase",
                  paddingTop: 2,
                }}
              >
                particulars
              </Text>
            </View>

            <View
              style={{
                width: "10%",
                borderRight: "1px solid black",
                paddingTop: "1px",
              }}
            >
              <Text
                style={{
                  fontSize: 8,
                  textAlign: "center",
                  textTransform: "uppercase",
                  paddingTop: 2,
                }}
              >
                hsn/sac
              </Text>
            </View>

            <View
              style={{
                width: "10%",
                borderRight: "1px solid black",
                paddingTop: "1px",
              }}
            >
              <Text
                style={{
                  fontSize: 8,
                  textAlign: "center",
                  textTransform: "uppercase",
                  paddingTop: 2,
                }}
              >
                tax rate
              </Text>
            </View>

            <View
              style={{
                width: "10%",
                borderRight: "1px solid black",
                paddingTop: "1px",
              }}
            >
              <Text
                style={{
                  fontSize: 8,
                  textAlign: "center",
                  textTransform: "uppercase",
                  paddingTop: 2,
                }}
              >
                qnty
              </Text>
            </View>

            <View
              style={{
                width: "10%",
                borderRight: "1px solid black",
                paddingTop: "1px",
              }}
            >
              <Text
                style={{
                  fontSize: 8,
                  textAlign: "center",
                  textTransform: "uppercase",
                  paddingTop: 2,
                }}
              >
                price
              </Text>
            </View>

            <View style={{ width: "15%" }}>
              <Text
                style={{
                  fontSize: 8,
                  textAlign: "center",
                  textTransform: "uppercase",
                  paddingTop: 2,
                }}
              >
                Amount
              </Text>
            </View>
          </View>

          {/* ===== Line items: rows ===== */}
          <View
            style={{
              minHeight: "30px",
              borderRight: "1px solid black",
              borderBottom: "1px solid black",
              borderLeft: "1px solid black",
            }}
          >
            {(invoiceData?.orderItems || []).map((ele, i) => (
              <View
                key={i}
                style={{
                  flexDirection: "row",
                  borderBottom: "1px solid black",
                  minHeight: "16px",
                }}
              >
                <View
                  style={{
                    width: "5%",
                    borderRight: "1px solid black",
                    paddingTop: "1px",
                  }}
                >
                  <Text
                    style={{
                      fontSize: 8,
                      textAlign: "center",
                      textTransform: "uppercase",
                      paddingTop: 2,
                    }}
                  >
                    {i + 1}
                  </Text>
                </View>

                <View
                  style={{
                    width: "40%",
                    borderRight: "1px solid black",
                    paddingTop: "1px",
                  }}
                >
                  <Text
                    style={{
                      fontSize: 8,
                      textAlign: "center",
                      textTransform: "uppercase",
                      paddingTop: 2,
                    }}
                  >
                    {getProductName(ele) || "-"}
                  </Text>
                </View>

                <View
                  style={{
                    width: "10%",
                    borderRight: "1px solid black",
                    paddingTop: "1px",
                  }}
                >
                  <Text
                    style={{
                      fontSize: 8,
                      textAlign: "center",
                      textTransform: "uppercase",
                      paddingTop: 2,
                    }}
                  >
                    {getProductHSN(ele) || "-"}
                  </Text>
                </View>

                <View
                  style={{
                    width: "10%",
                    borderRight: "1px solid black",
                    paddingTop: "1px",
                  }}
                >
                  <Text
                    style={{
                      fontSize: 8,
                      textAlign: "center",
                      textTransform: "uppercase",
                      paddingTop: 2,
                    }}
                  >
                    {parseRate(getProductGSTRate(ele)) || 0}
                  </Text>
                </View>

                <View
                  style={{
                    width: "10%",
                    borderRight: "1px solid black",
                    paddingTop: "1px",
                  }}
                >
                  <Text
                    style={{
                      fontSize: 8,
                      textAlign: "center",
                      textTransform: "uppercase",
                      paddingTop: 2,
                    }}
                  >
                    {F2(ele?.qty)}
                  </Text>
                </View>

                <View
                  style={{
                    width: "10%",
                    borderRight: "1px solid black",
                    paddingTop: "1px",
                  }}
                >
                  <Text
                    style={{
                      fontSize: 8,
                      textAlign: "center",
                      textTransform: "uppercase",
                      paddingTop: 2,
                    }}
                  >
                    {F2(ele?.price)}
                  </Text>
                </View>

                <View style={{ width: "15%" }}>
                  <Text
                    style={{
                      fontSize: 8,
                      textAlign: "center",
                      textTransform: "uppercase",
                      paddingTop: 2,
                    }}
                  >
                    {F2(N(ele?.qty) * N(ele?.price))}
                  </Text>
                </View>
              </View>
            ))}
          </View>

          {/* ===== Totals + HSN Summary wrapper ===== */}
          <View
            style={{
              flexDirection: "row",
              borderTop: "1px solid black",
              marginTop: "25px",
              borderLeft: "1px solid black",
              borderRight: "1px solid black",
            }}
          >
            {/* ===== Left 70%: Amount in words + HSN Summary ===== */}
            <View style={{ width: "70%", borderRight: "1px solid black" }}>
              {/* Amount in words */}
              <View style={{ minHeight: "16px" }}>
                <Text
                  style={{
                    fontSize: 8,
                    padding: 2,
                    fontWeight: "bold",
                    color: "black",
                    textAlign: "center",
                    textTransform: "uppercase",
                  }}
                >
                  Total: {amountInWords}
                </Text>
              </View>

              {/* HSN Summary Header */}
              <View
                style={{
                  flexDirection: "row",
                  height: "15px",
                  borderTop: "1px solid black",
                }}
              >
                <View
                  style={{
                    width: "25%",
                    borderBottom: "1px solid black",
                    borderRight: "1px solid black",
                  }}
                >
                  <Text
                    style={{
                      fontSize: 8,
                      fontWeight: "bold",
                      textAlign: "center",
                      textTransform: "uppercase",
                      paddingTop: 2,
                    }}
                  >
                    hsn/sac
                  </Text>
                </View>
                <View
                  style={{
                    width: "18%",
                    borderBottom: "1px solid black",
                    borderRight: "1px solid black",
                  }}
                >
                  <Text
                    style={{
                      fontSize: 8,
                      fontWeight: "bold",
                      textAlign: "center",
                      textTransform: "uppercase",
                      paddingTop: 2,
                    }}
                  >
                    taxable value
                  </Text>
                </View>
                <View
                  style={{
                    width: "12%",
                    borderBottom: "1px solid black",
                    borderRight: "1px solid black",
                  }}
                >
                  <Text
                    style={{
                      fontSize: 8,
                      fontWeight: "bold",
                      textAlign: "center",
                      textTransform: "uppercase",
                      paddingTop: 2,
                    }}
                  >
                    tax rate
                  </Text>
                </View>
                <View
                  style={{
                    width: "10%",
                    borderBottom: "1px solid black",
                    borderRight: "1px solid black",
                  }}
                >
                  <Text
                    style={{
                      fontSize: 8,
                      fontWeight: "bold",
                      textAlign: "center",
                      textTransform: "uppercase",
                      paddingTop: 2,
                    }}
                  >
                    cgst
                  </Text>
                </View>
                <View
                  style={{
                    width: "10%",
                    borderBottom: "1px solid black",
                    borderRight: "1px solid black",
                  }}
                >
                  <Text
                    style={{
                      fontSize: 8,
                      fontWeight: "bold",
                      textAlign: "center",
                      textTransform: "uppercase",
                      paddingTop: 2,
                    }}
                  >
                    sgst
                  </Text>
                </View>
                <View
                  style={{
                    width: "10%",
                    borderBottom: "1px solid black",
                    borderRight: "1px solid black",
                  }}
                >
                  <Text
                    style={{
                      fontSize: 8,
                      fontWeight: "bold",
                      textAlign: "center",
                      textTransform: "uppercase",
                      paddingTop: 2,
                    }}
                  >
                    igst
                  </Text>
                </View>
                <View style={{ width: "17%", borderBottom: "1px solid black" }}>
                  <Text
                    style={{
                      fontSize: 8,
                      fontWeight: "bold",
                      textAlign: "center",
                      textTransform: "uppercase",
                      paddingTop: 2,
                    }}
                  >
                    total
                  </Text>
                </View>
              </View>

              {/* HSN Summary Rows */}
              {hsnRows.map((row, i) => (
                <View key={i} style={{ flexDirection: "row", height: "15px" }}>
                  <View
                    style={{
                      width: "25%",
                      borderBottom: "1px solid black",
                      borderRight: "1px solid black",
                    }}
                  >
                    <Text
                      style={{
                        fontSize: 8,
                        textAlign: "center",
                        textTransform: "uppercase",
                        paddingTop: 2,
                      }}
                    >
                      {row.hsn}
                    </Text>
                  </View>

                  <View
                    style={{
                      width: "18%",
                      borderBottom: "1px solid black",
                      borderRight: "1px solid black",
                    }}
                  >
                    <Text
                      style={{
                        fontSize: 8,
                        textAlign: "center",
                        textTransform: "uppercase",
                        paddingTop: 2,
                      }}
                    >
                      {F2(row.taxable)}
                    </Text>
                  </View>

                  <View
                    style={{
                      width: "12%",
                      borderBottom: "1px solid black",
                      borderRight: "1px solid black",
                    }}
                  >
                    <Text
                      style={{
                        fontSize: 8,
                        textAlign: "center",
                        textTransform: "uppercase",
                        paddingTop: 2,
                      }}
                    >
                      {F2(row.rate)} %
                    </Text>
                  </View>

                  <View
                    style={{
                      width: "10%",
                      borderBottom: "1px solid black",
                      borderRight: "1px solid black",
                    }}
                  >
                    <Text
                      style={{
                        fontSize: 8,
                        textAlign: "center",
                        textTransform: "uppercase",
                        paddingTop: 2,
                      }}
                    >
                      {row.cgst ? F2(row.cgst) : ""}
                    </Text>
                  </View>

                  <View
                    style={{
                      width: "10%",
                      borderBottom: "1px solid black",
                      borderRight: "1px solid black",
                    }}
                  >
                    <Text
                      style={{
                        fontSize: 8,
                        textAlign: "center",
                        textTransform: "uppercase",
                        paddingTop: 2,
                      }}
                    >
                      {row.sgst ? F2(row.sgst) : ""}
                    </Text>
                  </View>

                  <View
                    style={{
                      width: "10%",
                      borderBottom: "1px solid black",
                      borderRight: "1px solid black",
                    }}
                  >
                    <Text
                      style={{
                        fontSize: 8,
                        textAlign: "center",
                        textTransform: "uppercase",
                        paddingTop: 2,
                      }}
                    >
                      {row.igst ? F2(row.igst) : ""}
                    </Text>
                  </View>

                  <View
                    style={{ width: "17%", borderBottom: "1px solid black" }}
                  >
                    <Text
                      style={{
                        fontSize: 8,
                        textAlign: "center",
                        textTransform: "uppercase",
                        paddingTop: 2,
                      }}
                    >
                      {F2(row.total)}
                    </Text>
                  </View>
                </View>
              ))}

              {/* HSN Summary: Total row */}
              <View
                style={{
                  flexDirection: "row",
                  height: "15px",
                  borderBottom: "1px solid black",
                }}
              >
                <View style={{ width: "25%", borderRight: "1px solid black" }}>
                  <Text
                    style={{
                      fontSize: 8,
                      fontWeight: "bold",
                      textAlign: "center",
                      textTransform: "uppercase",
                      paddingTop: 2,
                    }}
                  >
                    total
                  </Text>
                </View>

                <View style={{ width: "18%", borderRight: "1px solid black" }}>
                  <Text
                    style={{
                      fontSize: 8,
                      fontWeight: "bold",
                      textAlign: "center",
                      textTransform: "uppercase",
                    }}
                  >
                    {F2(hsnTaxableSum)}
                  </Text>
                </View>

                <View style={{ width: "12%", borderRight: "1px solid black" }}>
                  <Text />
                </View>

                {isIGST ? (
                  <>
                    <View
                      style={{ width: "10%", borderRight: "1px solid black" }}
                    >
                      <Text />
                    </View>
                    <View
                      style={{ width: "10%", borderRight: "1px solid black" }}
                    >
                      <Text />
                    </View>
                    <View
                      style={{ width: "10%", borderRight: "1px solid black" }}
                    >
                      <Text
                        style={{
                          fontSize: 8,
                          fontWeight: "bold",
                          textAlign: "center",
                          textTransform: "uppercase",
                          paddingTop: 2,
                        }}
                      >
                        {F2(hsnIGST)}
                      </Text>
                    </View>
                  </>
                ) : (
                  <>
                    <View
                      style={{ width: "10%", borderRight: "1px solid black" }}
                    >
                      <Text
                        style={{
                          fontSize: 8,
                          fontWeight: "bold",
                          textAlign: "center",
                          textTransform: "uppercase",
                          paddingTop: 2,
                        }}
                      >
                        {F2(hsnCGST)}
                      </Text>
                    </View>
                    <View
                      style={{ width: "10%", borderRight: "1px solid black" }}
                    >
                      <Text
                        style={{
                          fontSize: 8,
                          fontWeight: "bold",
                          textAlign: "center",
                          textTransform: "uppercase",
                          paddingTop: 2,
                        }}
                      >
                        {F2(hsnSGST)}
                      </Text>
                    </View>
                    <View
                      style={{ width: "10%", borderRight: "1px solid black" }}
                    >
                      <Text />
                    </View>
                  </>
                )}

                {/* Pre-round total from HSN */}
                <View style={{ width: "17%" }}>
                  <Text
                    style={{
                      fontSize: 8,
                      fontWeight: "bold",
                      textAlign: "center",
                      textTransform: "uppercase",
                    }}
                  >
                    {F2(hsnPreRoundGrand)}
                  </Text>
                </View>
              </View>

              {/* Bank details (left block bottom) */}
              <View style={{ flexDirection: "row", padding: 4 }}>
                <View style={{ width: "25%", paddingHorizontal: 4 }}>
                  <Text
                    style={{
                      fontSize: 8,
                      color: "black",
                      textTransform: "uppercase",
                      fontWeight: "bold",
                      marginBottom: 2,
                    }}
                  >
                    Bank Name:
                  </Text>
                  <Text style={{ fontSize: 8, color: "black" }}>
                    {bank?.bankName || "-"}
                  </Text>
                </View>

                <View style={{ width: "25%", paddingHorizontal: 4 }}>
                  <Text
                    style={{
                      fontSize: 8,
                      color: "black",
                      textTransform: "uppercase",
                      fontWeight: "bold",
                      marginBottom: 2,
                    }}
                  >
                    A/C No.:
                  </Text>
                  <Text style={{ fontSize: 8, color: "black" }}>
                    {bank?.accountNumber || "-"}
                  </Text>
                </View>

                <View style={{ width: "25%", paddingHorizontal: 4 }}>
                  <Text
                    style={{
                      fontSize: 8,
                      color: "black",
                      textTransform: "uppercase",
                      fontWeight: "bold",
                      marginBottom: 2,
                    }}
                  >
                    IFSC Code:
                  </Text>
                  <Text style={{ fontSize: 8, color: "black" }}>
                    {bank?.bankIFSC || "-"}
                  </Text>
                </View>

                <View style={{ width: "25%", paddingHorizontal: 4 }}>
                  <Text
                    style={{
                      fontSize: 8,
                      color: "black",
                      textTransform: "uppercase",
                      fontWeight: "bold",
                      marginBottom: 2,
                    }}
                  >
                    MICR No:
                  </Text>
                  <Text style={{ fontSize: 8, color: "black" }}>
                    {bank?.bankMicr || "-"}
                  </Text>
                </View>
              </View>
            </View>

            {/* ===== Right 30%: Summary box ===== */}
            <View
              style={{
                width: "30%",
                borderRight: "1px solid black",
                padding: 3,
              }}
            >
              {/* Basic / Discounts / Charges / Net Taxable */}
              <View
                style={{
                  flexDirection: "row",
                  justifyContent: "space-between",
                  paddingBottom: 3,
                }}
              >
                <Text style={{ fontSize: 8 }}>Basic</Text>
                <Text style={{ fontSize: 8 }}>
                  {F2(basicTotalBeforeDiscount)}
                </Text>
              </View>

              {discountsTotal > 0 && (
                <View
                  style={{
                    flexDirection: "row",
                    justifyContent: "space-between",
                    paddingBottom: 3,
                  }}
                >
                  <Text style={{ fontSize: 8 }}>Discounts</Text>
                  <Text style={{ fontSize: 8 }}>-{F2(discountsTotal)}</Text>
                </View>
              )}

              {chargesTotal > 0 && (
                <View
                  style={{
                    flexDirection: "row",
                    justifyContent: "space-between",
                    paddingBottom: 3,
                  }}
                >
                  <Text style={{ fontSize: 8 }}>Charges</Text>
                  <Text style={{ fontSize: 8 }}>{F2(chargesTotal)}</Text>
                </View>
              )}

              {/* Net Taxable (equals HSN taxable sum) */}
              <View
                style={{
                  flexDirection: "row",
                  justifyContent: "space-between",
                  paddingBottom: 3,
                  borderBottom: "1px solid black",
                  marginBottom: 2,
                }}
              >
                <Text style={{ fontSize: 8, fontWeight: "bold" }}>
                  Taxable Value
                </Text>
                <Text style={{ fontSize: 8, fontWeight: "bold" }}>
                  {F2(hsnTaxableSum)}
                </Text>
              </View>

              {/* Taxes from HSN */}
              {isIGST ? (
                <View
                  style={{
                    flexDirection: "row",
                    justifyContent: "space-between",
                    paddingBottom: 3,
                  }}
                >
                  <Text style={{ fontSize: 8, fontWeight: "bold" }}>IGST</Text>
                  <Text style={{ fontSize: 8, fontWeight: "bold" }}>
                    {F2(hsnIGST)}
                  </Text>
                </View>
              ) : (
                <>
                  <View
                    style={{
                      flexDirection: "row",
                      justifyContent: "space-between",
                      paddingBottom: 3,
                    }}
                  >
                    <Text style={{ fontSize: 8, fontWeight: "bold" }}>
                      CGST
                    </Text>
                    <Text style={{ fontSize: 8, fontWeight: "bold" }}>
                      {F2(hsnCGST)}
                    </Text>
                  </View>
                  <View
                    style={{
                      flexDirection: "row",
                      justifyContent: "space-between",
                      paddingBottom: 3,
                    }}
                  >
                    <Text style={{ fontSize: 8, fontWeight: "bold" }}>
                      SGST
                    </Text>
                    <Text style={{ fontSize: 8, fontWeight: "bold" }}>
                      {F2(hsnSGST)}
                    </Text>
                  </View>
                </>
              )}

              {/* RoundOff */}
              <View
                style={{
                  flexDirection: "row",
                  justifyContent: "space-between",
                  paddingBottom: 3,
                  borderBottom: "1px solid black",
                }}
              >
                <Text style={{ fontSize: 8, fontWeight: "bold" }}>
                  RoundOff
                </Text>
                <Text style={{ fontSize: 8, fontWeight: "bold" }}>
                  {F2(computedRoundOff)}
                </Text>
              </View>

              {/* Grand Total */}
              <View
                style={{
                  flexDirection: "row",
                  justifyContent: "space-between",
                  paddingBottom: 3,
                  marginTop: 2,
                }}
              >
                <Text style={{ fontSize: 8, fontWeight: "bold" }}>
                  Grand Total
                </Text>
                <Text style={{ fontSize: 8, fontWeight: "bold" }}>
                  {F2(computedFinalGrand)}
                </Text>
              </View>
            </View>
          </View>

          {/* ===== Terms & Conditions + QR (bottom) and Signature box ===== */}
          <View
            style={{
              flexDirection: "row",
              borderBottom: "1px solid black",
              borderLeft: "1px solid black",
              borderRight: "1px solid black",
              minHeight: "100px",
            }}
          >
            {/* Left: T&C + payment line */}
            <View style={{ width: "70%", borderRight: "1px solid black" }}>
              <View
                style={{
                  flexDirection: "row",
                  padding: 3,
                  paddingTop: 1,
                  borderTop: "1px solid black",
                }}
              >
                <View style={{ width: "70%", paddingRight: 10 }}>
                  <Text
                    style={{
                      fontSize: 8,
                      fontWeight: "bold",
                      color: "black",
                      textTransform: "uppercase",
                    }}
                  >
                    Terms & Conditions:
                  </Text>
                  <Text
                    style={{
                      fontSize: 8,
                      fontWeight: "bold",
                      color: "black",
                      marginTop: 3,
                      minHeight: 40,
                      textTransform: "uppercase",
                    }}
                  >
                    {companyData?.termsAndCondition ||
                      BilData?.CompanyDetails?.termsAndCondition ||
                      "GOODS ONCE SOLD CANNOT BE TAKEN BACK, TRANSPORTATION RISK AT BUYER SIDE"}
                  </Text>

                  {/* inline payment info */}
                  <Text
                    style={{
                      display: "inline-block",
                      border: "1px solid #ccc",
                      padding: "4px 8px",
                      borderRadius: "4px",
                      fontSize: 8,
                      marginBottom: 16,
                    }}
                  >
                    <Text>
                      GPay No:{" "}
                      {gpay || "-"}{" "}
                      {"  "} | {"  "}
                      UPI ID:{" "}
                      {upiid || "-"}
                    </Text>
                  </Text>
                </View>

                {/* Right: QR */}
                <View
                  style={{
                    width: "30%",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "flex-start",
                    textAlign: "center",
                    color: "black",
                  }}
                >
                  <Text
                    style={{
                      marginBottom: 8,
                      fontWeight: "bold",
                      fontSize: 10,
                    }}
                  >
                    QR CODE
                  </Text>
                  {qrSrc ? (
                    <Image
                      style={{
                        width: "100px",
                        height: "100px",
                      }}
                      src={qrSrc}
                    />
                  ) : (
                    <Text style={{ fontSize: 9 }}>NO QR</Text>
                  )}
                </View>
              </View>
            </View>

            {/* Right: signature */}
            <View
              style={{
                width: "30%",
                borderRight: "1px solid black",
                padding: 3,
              }}
            >
              <View style={{ minHeight: 20 }}>
                <Text
                  style={{
                    fontSize: 8,
                    color: "black",
                    textTransform: "uppercase",
                  }}
                >
                  For
                </Text>
              </View>
              <View style={{ minHeight: 20 }}>
                <Text
                  style={{
                    fontSize: 8,
                    color: "black",
                    textAlign: "center",
                    textTransform: "uppercase",
                  }}
                >
                  {safeCompanyName}
                </Text>
              </View>
              <View style={{ minHeight: 60, justifyContent: "center" }}>
                {signSrc ? (
                  <Image
                    style={{ width: "100%", padding: "2px 2px" }}
                    src={signSrc}
                  />
                ) : (
                  <Text style={{ fontSize: 9, textAlign: "center" }}>
                    SIGNATURE
                  </Text>
                )}
              </View>
              <View style={{ minHeight: 20 }}>
                <Text
                  style={{
                    fontSize: 8,
                    color: "black",
                    textAlign: "right",
                    textTransform: "uppercase",
                  }}
                >
                  authorized signatory
                </Text>
              </View>
            </View>
          </View>

          {/* ===== CN Image (if any) ===== */}
          <View>
            {invoiceData?.CNImage && (
              <Image
                src={imgURL(invoiceData?.CNImage)}
                style={styles.cnimage}
              />
            )}
          </View>
        </View>
      </Page>
    </Document>
  );
};

export default LastestInvoice;
